import { getUsageState } from '../../pricing/usageStore';
import { formatUnitPence } from '../../pricing/money';
import React from 'react';
import HelpWalkthrough from '../../shared/HelpWalkthrough';
import '../helpStages.css';

// "How campaigns work" walkthrough: each slide is a small animated demo
// rather than a static icon. Slides remount per step, so the staggered
// entrance animations replay naturally.

const Tick = ({ size = 9 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden><polyline points="20 6 9 17 4 12" /></svg>
);

/* 1 ── an email assembling itself, block by block */
const DesignStage = () => (
  <div className="hs-float">
    <div className="hs-email">
      <div className="hs-email-hero hs-rise" style={{ animationDelay: '0.1s' }}>
        <span className="hs-line" style={{ width: 76, height: 7 }} />
        <span className="hs-line" style={{ width: 52 }} />
      </div>
      <div className="hs-email-img hs-rise" style={{ animationDelay: '0.3s' }} />
      <div className="hs-email-body">
        <span className="hs-line hs-rise" style={{ width: '92%', animationDelay: '0.48s' }} />
        <span className="hs-line hs-rise" style={{ width: '70%', animationDelay: '0.56s' }} />
        <span className="hs-btn-pill hs-pop" style={{ animationDelay: '0.78s' }} />
      </div>
    </div>
  </div>
);

/* 2 ── audience rows ticking in */
const AudienceStage = () => (
  <div className="hs-float">
    <div className="hs-panel">
      {[
        { name: 'Sophie', colour: '#C2557A', w: 64, d: 0.1 },
        { name: 'Amelia', colour: '#5A8A78', w: 78, d: 0.26 },
        { name: 'Hannah', colour: '#6D597A', w: 56, d: 0.42 },
      ].map((r) => (
        <div key={r.name} className="hs-row hs-rise" style={{ animationDelay: `${r.d}s` }}>
          <span className="hs-avatar" style={{ background: r.colour, opacity: 0.85 }} />
          <span className="hs-line" style={{ width: r.w, margin: 0 }} />
          <span className="hs-tick hs-pop" style={{ animationDelay: `${r.d + 0.45}s` }}><Tick /></span>
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 9 }}>
        <span className="hs-chip hs-pop" style={{ background: 'rgba(0,126,160,0.1)', color: '#007EA0', animationDelay: '1.15s' }}>
          Opted-in clients only
        </span>
      </div>
    </div>
  </div>
);

/* 3 ── the compliance footer with a shield drawing its tick */
const ComplianceStage = () => (
  <div className="hs-float" style={{ position: 'relative' }}>
    <div className="hs-panel hs-rise" style={{ width: 188, animationDelay: '0.1s', textAlign: 'center', paddingTop: 16 }}>
      <span className="hs-line" style={{ width: 110, margin: '0 auto' }} />
      <span className="hs-line" style={{ width: 84, margin: '6px auto 0' }} />
      <div className="hs-rise" style={{ marginTop: 11, paddingTop: 10, borderTop: '1px solid #F2F2F7', animationDelay: '0.4s' }}>
        <span style={{ fontSize: 9.5, fontWeight: 600, color: '#8E8E93', textDecoration: 'underline', marginRight: 8 }}>Unsubscribe</span>
        <span style={{ fontSize: 9.5, fontWeight: 600, color: '#8E8E93', textDecoration: 'underline' }}>Update preferences</span>
      </div>
    </div>
    <span className="hs-shield hs-pop" style={{ position: 'absolute', top: -14, right: -14, animationDelay: '0.65s' }}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M12 22s8-3.5 8-10V5l-8-3-8 3v7c0 6.5 8 10 8 10z" />
        <polyline className="hs-draw" points="8.5 12 11 14.5 15.5 9.5" />
      </svg>
    </span>
  </div>
);

/* 4 ── what a campaign costs, in pounds (prices from the API when known) */
const CreditsStage = () => {
  const known = getUsageState().usage
  const prices = (known && known.prices) || {}
  return (
  <div className="hs-float" style={{ position: 'relative' }}>
    <div className="hs-panel" style={{ width: 208 }}>
      <div className="hs-row hs-rise" style={{ borderTop: 'none', padding: '2px 0 9px', animationDelay: '0.1s' }}>
        <span className="hs-credit-icon">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </span>
        <span className="hs-credit-total">£24.80</span>
        <span className="hs-credit-unit">balance</span>
      </div>
      {[
        { label: 'Marketing email', cost: prices.emailPence != null ? formatUnitPence(prices.emailPence) : '', d: 0.36 },
        { label: 'Marketing text', cost: prices.textPence != null ? formatUnitPence(prices.textPence) : '', d: 0.52 },
      ].map((r) => (
        <div key={r.label} className="hs-row hs-rise" style={{ animationDelay: `${r.d}s` }}>
          <span className="hs-credit-name">{r.label}</span>
          <span className="hs-credit-cost hs-pop" style={{ animationDelay: `${r.d + 0.3}s` }}>{r.cost}</span>
        </div>
      ))}
    </div>
    {/* Wrapper does the centring: hs-pop animates transform to scale(1)
        with `forwards`, which would overwrite a translateX on the chip. */}
    <span style={{ position: 'absolute', left: 0, right: 0, bottom: -15, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
      <span
        className="hs-chip hs-pop"
        style={{ whiteSpace: 'nowrap', background: '#fff', color: '#5A8A78', boxShadow: '0 4px 14px rgba(26,26,30,0.14)', animationDelay: '1.05s' }}
      >
        <Tick size={8} /> Reminders never stop
      </span>
    </span>
  </div>
  );
};

/* 5 ── the funnel filling, ending on bookings */
const ResultsStage = () => (
  <div className="hs-float">
    <div className="hs-panel" style={{ width: 204 }}>
      {[
        { label: 'SENT', w: '100%', colour: '#29282D', d: 0.1 },
        { label: 'OPENED', w: '64%', colour: '#007EA0', d: 0.3 },
        { label: 'CLICKED', w: '34%', colour: '#00ACC9', d: 0.5 },
        { label: 'BOOKED', w: '18%', colour: '#005F79', d: 0.7 },
      ].map((b) => (
        <div key={b.label} className="hs-row" style={{ borderTop: 'none', padding: '4.5px 0' }}>
          <span className="hs-funnel-label">{b.label}</span>
          <span className="hs-funnel-track">
            <span className="hs-funnel-fill hs-bar" style={{ width: b.w, background: b.colour, display: 'block', animationDelay: `${b.d}s` }} />
          </span>
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 7 }}>
        <span className="hs-chip hs-pop" style={{ background: 'rgba(0,126,160,0.1)', color: '#007EA0', animationDelay: '1.2s' }}>
          <Tick size={8} /> 9 bookings
        </span>
      </div>
    </div>
  </div>
);

const SLIDES = [
  {
    key: 'design',
    title: 'Design beautiful emails in minutes',
    body: 'Start from a template, a saved design or a blank canvas, then make it yours with your services, products, vouchers, reviews and photos. Save any design to My Templates for next time.',
    Stage: DesignStage,
  },
  {
    key: 'audience',
    title: 'Choose exactly who receives it',
    body: 'Send to a smart segment like VIPs or quiet clients, or hand-pick individuals. Campaigns only ever go to clients who are opted in to marketing.',
    Stage: AudienceStage,
  },
  {
    key: 'compliance',
    title: 'UK marketing rules, handled',
    body: 'Every email automatically carries your business details and an unsubscribe link, and opted-out clients are excluded at send. You stay on the right side of the rules without thinking about it.',
    Stage: ComplianceStage,
  },
  {
    key: 'credits',
    title: 'Campaigns use your message balance',
    body: 'Marketing emails and texts come out of your free monthly allowance first, then your message balance, which never expires. Appointment reminders never stop: they keep sending even if your balance runs out.',
    Stage: CreditsStage,
  },
  {
    key: 'results',
    title: 'See what comes back',
    body: 'Open any sent campaign for its funnel from delivered through to booked, and use the Performance tab to track how your marketing performs over time.',
    Stage: ResultsStage,
  },
];

const CampaignsHelp = ({ onClose }) => (
  <HelpWalkthrough eyebrow="How campaigns work" slides={SLIDES} onClose={onClose} />
);

export default CampaignsHelp;
